// Main-thread side of the audit worker: a two-level priority queue in front of one
// worker. `auditNow()` (the selected blueprint) jumps ahead of `auditBackground()` jobs
// (the status-dot sweep); jobs run one at a time.
import type { AuditRequest, AuditResponse } from './worker';
import type { AuditResult } from './pipeline';

export interface AuditJob { name: string; sbp: ArrayBuffer; sbpcfg?: ArrayBuffer; inputs?: string[] }

interface Pending { id: number; job: AuditJob; resolve: (r: AuditResult) => void; reject: (e: Error) => void }

/** Thrown to every queued job when the worker itself fails (could not load, crashed). */
export class WorkerError extends Error {
  override name = 'WorkerError';
}

// worker.js sits next to index.html: a second bundler entry point in production
// (scripts/build.ts, sharing chunks with the app) and built on demand by the dev server.
let worker: Worker | null = null;
let nextId = 1;
const urgent: Pending[] = [];
const background: Pending[] = [];
let running: Pending | null = null;

/** The live worker, started on first use and restarted after a failure. */
function getWorker(): Worker {
  if (worker) return worker;
  const w = new Worker(new URL('worker.js', document.baseURI), { type: 'module' });
  w.onmessage = (ev: MessageEvent<AuditResponse>) => {
    const msg = ev.data;
    if (w !== worker || !running || running.id !== msg.id) return;
    const p = running;
    running = null;
    if ('error' in msg) p.reject(new Error(msg.error));
    else p.resolve(msg.result);
    pump();
  };
  w.onerror = (ev) => {
    // The worker is unusable (worker.js missing, a crash outside the message handler): drop
    // it, fail everything waiting so the UI can say so instead of showing "auditing" forever,
    // and start a fresh worker for the next job.
    if (w !== worker) return;
    worker = null;
    w.terminate();
    const err = new WorkerError(ev.message ? `audit worker failed: ${ev.message}` : 'audit worker failed to load (worker.js)');
    const waiting = [running, ...urgent, ...background].filter((p): p is Pending => !!p);
    running = null;
    urgent.length = 0;
    background.length = 0;
    for (const p of waiting) p.reject(err);
  };
  worker = w;
  return w;
}

function pump() {
  if (running) return;
  const p = urgent.shift() ?? background.shift();
  if (!p) return;
  running = p;
  const req: AuditRequest = { id: p.id, name: p.job.name, sbp: p.job.sbp, sbpcfg: p.job.sbpcfg, inputs: p.job.inputs ?? [] };
  // The buffers are copied, not transferred: callers keep them for re-audits with overrides.
  getWorker().postMessage(req);
}

function enqueue(queue: Pending[], job: AuditJob): Promise<AuditResult> {
  return new Promise((resolve, reject) => {
    queue.push({ id: nextId++, job, resolve, reject });
    pump();
  });
}

/** Audit now (ahead of any background work). */
export const auditNow = (job: AuditJob) => enqueue(urgent, job);
/** Audit when nothing more important is waiting. */
export const auditBackground = (job: AuditJob) => enqueue(background, job);
